import { FC, ReactNode } from 'react'
import { Section } from './Section.js'
import { Editor } from './Editor.js'
import { Finder } from './Finder.js'
import { Group } from './Group.js'
import { Tab } from './Tab.js'
import { Card } from './Card.js'
import { Stack } from '../Stack/index.js'
import { Container } from './Container.js'
import { AuthPage } from '../AuthPage/index.js'
import { Onboarding } from '../Onboarding/index.js'
import {
  Router,
  useCmsParams,
  useLocation,
  useSearchParams,
} from '../../hooks/useCmsParams.js'
import { colors } from '../../tokens/colors.js'

type CmsBodyParams = {
  children: ReactNode
  name?: string
  logo?: string
  followMe?: boolean
  leadComponent?: ReactNode
  trailComponent?: ReactNode
  hideAccountSettings?: boolean
  hideWorkspaceSettings?: boolean
  hideDevelopmentControls?: boolean
  logoutEndpoint?: string
}

const Provider: FC<{ base?: string; children: ReactNode }> = ({
  base,
  children,
}) => {
  return <Router base={base}>{children}</Router>
}

const Body: FC<CmsBodyParams> = ({
  children,
  name,
  logo,
  followMe = true,
  leadComponent,
  trailComponent,
  hideAccountSettings,
  hideWorkspaceSettings,
  hideDevelopmentControls,
  logoutEndpoint,
}) => {
  return (
    <Stack
      direction="column"
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'relative',
        background: `linear-gradient(0deg, ${colors.black5} 0%, ${colors.black5} 100%), ${colors.neutralInverted100}`,
      }}
    >
      <Container
        name={name}
        followMe={followMe}
        leadComponent={leadComponent}
        trailComponent={trailComponent}
        hideAccountSettings={hideAccountSettings}
        hideWorkspaceSettings={hideWorkspaceSettings}
        hideDevelopmentControls={hideDevelopmentControls}
        logo={logo}
        logoutEndpoint={logoutEndpoint}
      >
        {children}
      </Container>
    </Stack>
  )
}

export const Cms: React.FC<CmsBodyParams & { base?: string }> & {
  Section: typeof Section
  Finder: typeof Finder
  Editor: typeof Editor
  Group: typeof Group
  Card: typeof Card
  Tab: typeof Tab
  Auth: typeof AuthPage
  Onboarding: typeof Onboarding
  Provider: typeof Provider
  Body: typeof Body
} = ({
  children,
  base,
  followMe,
  leadComponent,
  logo,
  name,
  trailComponent,
}) => {
  return (
    <Cms.Provider base={base}>
      <Cms.Body
        name={name}
        followMe={followMe}
        leadComponent={leadComponent}
        trailComponent={trailComponent}
        logo={logo}
      >
        {children}
      </Cms.Body>
    </Cms.Provider>
  )
}

Cms.Section = Section
Cms.Editor = Editor
Cms.Finder = Finder
Cms.Group = Group
Cms.Tab = Tab
Cms.Auth = AuthPage
Cms.Onboarding = Onboarding
Cms.Provider = Provider
Cms.Body = Body
Cms.Card = Card

export { Router, useCmsParams, useLocation, useSearchParams, Card }
